import { useState, useEffect, useCallback, memo } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { MapPin, Plus, Edit, Trash2, Loader2, Phone, Building2, Globe } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Memoized form component to prevent re-renders causing input lag
const StationFormFields = memo(({ formData, onChange }) => {
  const handleChange = useCallback((field, value) => {
    onChange(field, value);
  }, [onChange]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Station Name *</Label>
          <Input
            defaultValue={formData.name}
            onBlur={(e) => handleChange('name', e.target.value)}
            placeholder="e.g., Harare Central Terminal"
            required
            data-testid="station-name-input"
          />
        </div>
        <div>
          <Label>Station Code *</Label>
          <Input
            defaultValue={formData.code}
            onBlur={(e) => handleChange('code', e.target.value.toUpperCase())}
            placeholder="e.g., HRE"
            maxLength={5}
            required
            data-testid="station-code-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>City *</Label>
          <Input
            defaultValue={formData.city}
            onBlur={(e) => handleChange('city', e.target.value)}
            placeholder="e.g., Harare"
            required
            data-testid="station-city-input"
          />
        </div>
        <div>
          <Label>Country *</Label>
          <Input
            defaultValue={formData.country}
            onBlur={(e) => handleChange('country', e.target.value)}
            placeholder="e.g., Zimbabwe"
            required
            data-testid="station-country-input"
          />
        </div>
      </div>

      <div>
        <Label>Address</Label>
        <Input
          defaultValue={formData.address}
          onBlur={(e) => handleChange('address', e.target.value)}
          placeholder="e.g., 4th Street & Robert Mugabe Road"
          data-testid="station-address-input"
        />
      </div>

      <div>
        <Label>Phone Number</Label>
        <Input
          defaultValue={formData.phone}
          onBlur={(e) => handleChange('phone', e.target.value)}
          placeholder="e.g., +263771234567"
          data-testid="station-phone-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Latitude</Label>
          <Input
            type="number"
            step="0.0001"
            defaultValue={formData.lat}
            onBlur={(e) => handleChange('lat', e.target.value)}
            placeholder="e.g., -17.8292"
            data-testid="station-lat-input"
          />
        </div>
        <div>
          <Label>Longitude</Label>
          <Input
            type="number"
            step="0.0001"
            defaultValue={formData.lng}
            onBlur={(e) => handleChange('lng', e.target.value)}
            placeholder="e.g., 31.0522"
            data-testid="station-lng-input"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          id="is_border"
          checked={formData.is_border}
          onCheckedChange={(checked) => handleChange('is_border', checked)}
          data-testid="station-border-checkbox"
        />
        <Label htmlFor="is_border">This is a border post station</Label>
      </div>
    </div>
  );
});

StationFormFields.displayName = 'StationFormFields';

const AdminStations = () => {
  const { token } = useAuth();
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingStation, setEditingStation] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    city: '',
    country: 'Zimbabwe',
    address: '',
    phone: '',
    is_border: false,
    lat: '',
    lng: ''
  });

  useEffect(() => {
    fetchStations();
  }, []);

  const fetchStations = async () => {
    try {
      const response = await axios.get(`${API_URL}/stations`);
      setStations(response.data);
    } catch (error) {
      toast.error('Failed to load stations');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API_URL}/stations`, {
        ...formData,
        lat: formData.lat ? parseFloat(formData.lat) : null,
        lng: formData.lng ? parseFloat(formData.lng) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Station created');
      setShowCreate(false);
      resetForm();
      fetchStations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create station');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/stations/${editingStation.id}`, {
        ...formData,
        lat: formData.lat ? parseFloat(formData.lat) : null,
        lng: formData.lng ? parseFloat(formData.lng) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Station updated');
      setShowEdit(false);
      setEditingStation(null);
      resetForm();
      fetchStations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update station');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (stationId) => {
    if (!window.confirm('Are you sure you want to delete this station?')) return;
    try {
      await axios.delete(`${API_URL}/stations/${stationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Station deleted');
      fetchStations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete station');
    }
  };

  const startEdit = (station) => {
    setEditingStation(station);
    setFormData({
      name: station.name,
      code: station.code,
      city: station.city,
      country: station.country,
      address: station.address || '',
      phone: station.phone || '',
      is_border: station.is_border || false,
      lat: station.lat ? String(station.lat) : '',
      lng: station.lng ? String(station.lng) : ''
    });
    setShowEdit(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      city: '',
      country: 'Zimbabwe',
      address: '',
      phone: '',
      is_border: false,
      lat: '',
      lng: ''
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-panashe-red" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-stations-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold">Manage Stations</h1>
            <p className="text-slate-600">{stations.length} stations configured</p>
          </div>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="btn-panashe" onClick={() => { resetForm(); setShowCreate(true); }} data-testid="add-station-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Station
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create New Station</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate}>
                <StationFormFields formData={formData} onChange={handleFieldChange} />
                <Button type="submit" className="w-full btn-panashe mt-4" disabled={saving} data-testid="submit-station-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Create Station
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {stations.map(station => (
            <Card key={station.id} className="hover:shadow-md transition-shadow" data-testid={`station-card-${station.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-panashe-red/10 rounded-lg flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-panashe-red" />
                    </div>
                    <div>
                      <h3 className="font-bold">{station.name}</h3>
                      <Badge variant="outline" className="text-xs">{station.code}</Badge>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => startEdit(station)} data-testid={`edit-station-${station.id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(station.id)} data-testid={`delete-station-${station.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-1 text-sm text-slate-600">
                  <p className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    {station.city}, {station.country}
                  </p>
                  {station.phone && (
                    <p className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {station.phone}
                    </p>
                  )}
                  {station.is_border && (
                    <Badge className="bg-orange-100 text-orange-800 mt-2">
                      <Globe className="h-3 w-3 mr-1" />
                      Border Post
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          {stations.length === 0 && (
            <Card className="col-span-full p-12 text-center">
              <Building2 className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold mb-2">No Stations Yet</h3>
              <p className="text-slate-600 mb-4">Create your first station to start configuring routes</p>
              <Button className="btn-panashe" onClick={() => setShowCreate(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add First Station
              </Button>
            </Card>
          )}
        </div>

        {/* Edit Dialog */}
        <Dialog open={showEdit} onOpenChange={setShowEdit}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Station</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEdit}>
              <StationFormFields formData={formData} onChange={handleFieldChange} />
              <Button type="submit" className="w-full btn-panashe mt-4" disabled={saving} data-testid="update-station-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Update Station
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminStations;
